/**
 * Feedback Card Component
 * Displays employee feedback with rating, comment, and metadata
 */

import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { StarIcon, Trash2Icon, BriefcaseIcon } from 'lucide-react';
import { format } from 'date-fns';

export interface Feedback {
  id: string;
  employeeId: string;
  fromUserId: string;
  fromUserName: string;
  fromUserRole?: string;
  rating: number;
  comment: string | null;
  jobId?: string | null;
  jobTitle?: string | null;
  createdAt: string;
}

interface FeedbackCardProps {
  feedback: Feedback;
  onDelete?: (feedback: Feedback) => void;
  className?: string;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, index) => (
        <StarIcon
          key={index}
          className={`h-4 w-4 ${
            index < rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700'
          }`}
        />
      ))}
      <span className="ml-2 text-sm font-medium text-foreground">{rating}/5</span>
    </div>
  );
}

export function FeedbackCard({ feedback, onDelete, className }: FeedbackCardProps) {
  const initials = feedback.fromUserName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Header with avatar, user info, and delete button */}
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{feedback.fromUserName}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <StarRating rating={feedback.rating} />
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(feedback.createdAt), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>
                {onDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onDelete(feedback)}
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Comment */}
          {feedback.comment && (
            <div className="pl-[52px]">
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {feedback.comment}
              </p>
            </div>
          )}

          {/* Related job link */}
          {feedback.jobId && feedback.jobTitle && (
            <div className="pl-[52px] pt-2 border-t">
              <div className="flex items-center gap-2 text-xs">
                <BriefcaseIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Related to:</span>
                <span className="font-medium text-foreground">{feedback.jobTitle}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
